import React, { useEffect, useMemo, useState } from "react";
import Moment from "moment";
import { useSelector } from "react-redux";

import {
  Box,
  Card,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  LinearProgress,
  Typography,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { customLocaleText } from "../../../utils/objects/CustomLocaleTextTables";

import useObtenerDatosdeCliente from "../../../hooks/obtenerDatosdeCliente";
import useGetDataRiderByCompany from "../../../hooks/getDataRiderByCompany";
import { useOrders } from "../../../hooks/useOrdersProviders/OrdersContext";

const DriversRanking = () => {
  const userUid = useSelector((store) => store.usuario);
  const uid = userUid?.user?.uid || null;

  const { userdata } = useObtenerDatosdeCliente(uid);
  const [companyId, setCompanyId] = useState("");

  // 🎛️ umbrales (jugables)
  const [yellowGap, setYellowGap] = useState(50);
  const [orangeGap, setOrangeGap] = useState(61);

  // Mes seleccionado (YYYY-MM)
  const [selectedMonth, setSelectedMonth] = useState(() =>
    Moment().format("YYYY-MM")
  );

  const initDateInitial = Moment().format("YYYY-MM-DD");
  const endDateInitial = Moment().format("YYYY-MM-DD");

  const {
    mergedOrders,
    loadingContext,
    setInitDate,
    setEndDate,
    setShouldSubscribe,
  } = useOrders({ initDateInitial, endDateInitial });

  const { ridersDataByCompany: companyRiders } =
    useGetDataRiderByCompany(companyId);

  useEffect(() => {
    if (userdata?.userCompanyID) setCompanyId(userdata.userCompanyID);
  }, [userdata]);

  useEffect(() => {
    setShouldSubscribe(true);
  }, [setShouldSubscribe]);

  useEffect(() => {
    const start = Moment(selectedMonth, "YYYY-MM")
      .startOf("month")
      .format("YYYY-MM-DD");
    const end = Moment(selectedMonth, "YYYY-MM")
      .endOf("month")
      .format("YYYY-MM-DD");
    setInitDate(start);
    setEndDate(end);
  }, [selectedMonth, setInitDate, setEndDate]);

  const monthOptions = useMemo(() => {
    const base = Moment();
    return Array.from({ length: 12 }).map((_, i) => {
      const d = base.clone().subtract(i, "months");
      return { key: d.format("YYYY-MM"), label: d.format("MMMM YYYY") };
    });
  }, []);

  const companyRidersMap = useMemo(() => {
    const m = new Map();
    if (Array.isArray(companyRiders)) {
      companyRiders.forEach((r) => {
        if (r?.id) m.set(String(r.id), r);
      });
    }
    return m;
  }, [companyRiders]);

  // ✅ Ranking + meta (posición, gap vs 2do, nivel)
  const rankingRows = useMemo(() => {
    if (!Array.isArray(mergedOrders) || mergedOrders.length === 0) return [];

    const counts = new Map(); // driverID -> deliveries

    for (const o of mergedOrders) {
      const driverID = (o?.driverID || "").trim();
      if (!driverID) continue;

      // solo conductores de la empresa
      if (!companyRidersMap.has(driverID)) continue;

      // entregadas
      const st = Number(o?.status);
      if (st !== 8) continue;

      counts.set(driverID, (counts.get(driverID) || 0) + 1);
    }

    let rows = Array.from(counts.entries())
      .map(([driverID, deliveries]) => {
        const r = companyRidersMap.get(driverID);
        return {
          id: driverID,
          name: r?.name || r?.displayName || r?.driverName || "(Sin nombre)",
          deliveries,
        };
      })
      .sort((a, b) => (b.deliveries || 0) - (a.deliveries || 0));

    const leader = rows[0]?.deliveries ?? 0;
    const second = rows[1]?.deliveries ?? leader;

    rows = rows.map((r, idx) => {
      const pos = idx + 1;

      // gap vs 2do lugar (tu ejemplo habla de quedar atrás del 2do)
      const gapVsSecond = pos <= 2 ? 0 : Math.max(0, second - r.deliveries);

      // nivel:
      // - top 2 siempre verde
      // - si no, amarillo/naranja según gap
      let level = "normal";
      if (pos <= 2) level = "green";
      else if (gapVsSecond > orangeGap) level = "orange";
      else if (gapVsSecond > yellowGap) level = "yellow";
      else level = "normal";

      return {
        ...r,
        pos,
        gapVsSecond,
        leader,
        second,
        level,
      };
    });

    return rows;
  }, [mergedOrders, companyRidersMap, yellowGap, orangeGap]);

  // 🎨 helpers para pintar celdas
  const cellClass = (level) => {
    if (level === "green") return "cell-green";
    if (level === "yellow") return "cell-yellow";
    if (level === "orange") return "cell-orange";
    return "";
  };

  const columns = useMemo(
    () => [
      {
        field: "pos",
        headerName: "#",
        width: 70,
        type: "number",
        align: "center",
        headerAlign: "center",
        cellClassName: (params) => cellClass(params.row.level),
      },
      {
        field: "name",
        headerName: "CONDUCTOR",
        flex: 1,
        minWidth: 260,
        // opcional: también pintar el nombre
        cellClassName: (params) => cellClass(params.row.level),
      },
      {
        field: "deliveries",
        headerName: "ENTREGAS",
        width: 140,
        type: "number",
        cellClassName: (params) => cellClass(params.row.level),
      },
      {
        field: "gapVsSecond",
        headerName: "NIVELAR CON",
        width: 120,
        type: "number",
        cellClassName: (params) => cellClass(params.row.level),
      },
    ],
    [yellowGap, orangeGap]
  );

  return (
    <Card>
      <CardHeader
        title="Ranking de Conductores"
        subheader="Top 1 y 2 en verde. Amarillo/Naranja según GAP vs 2do lugar."
      />
      <Divider />

      <Box
        sx={{
          p: 2,
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Mes</InputLabel>
          <Select
            value={selectedMonth}
            label="Mes"
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {monthOptions.map((m) => (
              <MenuItem key={m.key} value={m.key}>
                {m.label.charAt(0).toUpperCase() + m.label.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Amarillo ( Margen Top Autos )"
          type="number"
          value={yellowGap}
          onChange={(e) => setYellowGap(Number(e.target.value || 0))}
          sx={{ width: 180 }}
        />
        <TextField
          size="small"
          label="Naranja ( Margen Top Bicicletas )"
          type="number"
          value={orangeGap}
          onChange={(e) => setOrangeGap(Number(e.target.value || 0))}
          sx={{ width: 180 }}
        />

        <Box sx={{ flex: 1 }} />

        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Órdenes en rango:{" "}
          {Array.isArray(mergedOrders) ? mergedOrders.length : 0} · Ranking:{" "}
          {rankingRows.length}
        </Typography>
      </Box>

      {loadingContext ? <LinearProgress /> : null}

      <Box sx={{ width: "100%", minHeight: 520, p: 2, pt: 0 }}>
        <DataGrid
          rows={rankingRows}
          columns={columns}
          localeText={customLocaleText}
          loading={!!loadingContext}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 25 } },
            sorting: { sortModel: [{ field: "deliveries", sort: "desc" }] },
          }}
          sx={{
            // ✅ Pintar CELDAS (no fila)
            "& .cell-green": {
              backgroundColor: "#d1fae5",
              fontWeight: 700,
            },
            "& .cell-yellow": {
              backgroundColor: "#fef9c3",
              fontWeight: 700,
            },
            "& .cell-orange": {
              backgroundColor: "#ffedd5",
              fontWeight: 700,
            },

            // suaviza la vista
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
          }}
        />
      </Box>
    </Card>
  );
};

export default DriversRanking;
